import React, { useState } from 'react';
import {
  Box,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  HStack,
  VStack,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { Info, BookOpen } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import { POKER_GLOSSARY } from '../../services/training';

interface TermTooltipProps {
  term: string;
  children?: React.ReactNode;
  showIcon?: boolean;
  size?: 'sm' | 'md';
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const TermTooltip: React.FC<TermTooltipProps> = ({
  term,
  children,
  showIcon = true,
  size = 'sm',
  placement = 'top',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const glossaryKey = term.toLowerCase().replace(/[^a-z]/g, '');
  const glossaryEntry = POKER_GLOSSARY[glossaryKey] || findGlossaryEntry(term);
  
  if (!glossaryEntry) {
    return <>{children || term}</>;
  }
  
  const levelColors: Record<string, string> = {
    beginner: POKER_COLORS.darkGreen,
    intermediate: POKER_COLORS.gold,
    advanced: POKER_COLORS.cyan,
  };
  
  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement={placement}
      trigger="hover"
    >
      <PopoverTrigger>
        <HStack
          as="span"
          display="inline-flex"
          spacing={1}
          cursor="help"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          onClick={() => setIsOpen(!isOpen)}
          borderBottom="1px dashed"
          borderColor={`${POKER_COLORS.cyan}66`}
          _hover={{ borderColor: POKER_COLORS.cyan }}
        >
          <Text as="span" color="inherit">
            {children || term}
          </Text>
          {showIcon && (
            <Icon
              as={Info}
              boxSize={size === 'sm' ? 3 : 4}
              color={`${POKER_COLORS.cyan}88`}
              _hover={{ color: POKER_COLORS.cyan }}
            />
          )}
        </HStack>
      </PopoverTrigger>
      <PopoverContent
        bg={`${POKER_COLORS.darkTeal}f5`}
        border="1px solid"
        borderColor={`${POKER_COLORS.cyan}55`}
        borderRadius="lg"
        boxShadow={`0 4px 20px ${POKER_COLORS.darkTeal}88`}
        maxW="320px"
        _focus={{ outline: 'none' }}
      >
        <PopoverArrow bg={POKER_COLORS.darkTeal} />
        <PopoverBody p={4}>
          <VStack align="start" spacing={3}>
            <HStack justify="space-between" w="100%">
              <HStack spacing={2}>
                <Icon as={BookOpen} color={POKER_COLORS.cyan} boxSize={4} />
                <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="sm">
                  {glossaryEntry.term}
                </Text>
              </HStack>
              <Badge
                bg={`${levelColors[glossaryEntry.level]}33`}
                color={levelColors[glossaryEntry.level]}
                fontSize="xs"
                textTransform="capitalize"
              >
                {glossaryEntry.level}
              </Badge>
            </HStack>
            
            <Text color="gray.200" fontSize="sm" lineHeight="1.5">
              {glossaryEntry.definition}
            </Text>
            
            {glossaryEntry.example && (
              <Box
                p={2}
                bg={`${POKER_COLORS.darkTeal}aa`}
                borderRadius="md"
                borderLeft="3px solid"
                borderColor={POKER_COLORS.gold}
                w="100%"
              >
                <Text color="gray.400" fontSize="xs" fontWeight="bold" mb={1}>
                   Example:

                </Text>
                <Text color="gray.300" fontSize="xs" lineHeight="1.4">
                  {glossaryEntry.example}
                </Text>
              </Box>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

function findGlossaryEntry(term: string) {
  const normalizedTerm = term.toLowerCase();
  
  for (const [key, entry] of Object.entries(POKER_GLOSSARY)) {
    if (key === normalizedTerm) return entry;
    if (entry.term.toLowerCase().includes(normalizedTerm)) return entry;
    if (normalizedTerm.includes(key)) return entry;
  }
  
  const aliases: Record<string, string> = {
    'voluntarily put in pot': 'vpip',
    'preflop raise': 'pfr',
    'continuation bet': 'cbet',
    'pot odds': 'potOdds',
    'implied odds': 'impliedOdds',
    'aggression factor': 'aggFactor',
    'game theory optimal': 'gto',
    'minimum defense frequency': 'mdf',
    'stack to pot': 'spr',
    'semi bluff': 'semiBluff',
    'board texture': 'boardTexture',
  };
  
  const aliasKey = aliases[normalizedTerm];
  if (aliasKey && POKER_GLOSSARY[aliasKey]) {
    return POKER_GLOSSARY[aliasKey];
  }
  
  return null;
}

interface StatWithTooltipProps {
  label: string;
  value: string | number;
  term: string;
  color?: string;
  valueColor?: string;
}

export const StatWithTooltip: React.FC<StatWithTooltipProps> = ({
  label,
  value,
  term,
  color = 'gray.400',
  valueColor = POKER_COLORS.cyan,
}) => {
  return (
    <VStack spacing={1} align="center">
      <TermTooltip term={term}>
        <Text color={color} fontSize="xs">
          {label}
        </Text>
      </TermTooltip>
      <Text color={valueColor} fontWeight="bold" fontSize="xl">
        {typeof value === 'number' ? value.toFixed(0) : value}
        {typeof value === 'number' && '%'}
      </Text>
    </VStack>
  );
};

interface InlineTermProps {
  term: string;
  display?: string;
}

export const InlineTerm: React.FC<InlineTermProps> = ({ term, display }) => {
  return (
    <TermTooltip term={term} showIcon={false}>
      <Text as="span" color={POKER_COLORS.cyan} fontWeight="medium">
        {display || term}
      </Text>
    </TermTooltip>
  );
};

interface GlossarySectionProps {
  terms: string[];
  title?: string;
}

export const GlossarySection: React.FC<GlossarySectionProps> = ({
  terms,
  title = 'Key Terms',
}) => {
  const validTerms = terms
    .map(t => ({ key: t, entry: POKER_GLOSSARY[t] || findGlossaryEntry(t) }))
    .filter(t => t.entry);
  
  if (validTerms.length === 0) return null;
  
  const levelColors: Record<string, string> = {
    beginner: POKER_COLORS.darkGreen,
    intermediate: POKER_COLORS.gold,
    advanced: POKER_COLORS.cyan,
  };
  
  return (
    <Box
      p={4}
      bg={`${POKER_COLORS.darkTeal}55`}
      borderRadius="lg"
      border="1px solid"
      borderColor={`${POKER_COLORS.cyan}33`}
    >
      <HStack mb={3}>
        <Icon as={BookOpen} color={POKER_COLORS.cyan} />
        <Text color={POKER_COLORS.cyan} fontWeight="bold" fontSize="sm">
          {title}
        </Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {validTerms.map(({ key, entry }) => (
          <Box key={key}>
            <HStack justify="space-between" mb={1}>
              <Text color="white" fontWeight="medium" fontSize="sm">
                {entry!.term}
              </Text>
              <Badge
                size="sm"
                bg={`${levelColors[entry!.level]}33`}
                color={levelColors[entry!.level]}
                fontSize="xs"
              >
                {entry!.level}
              </Badge>
            </HStack>
            <Text color="gray.400" fontSize="xs" lineHeight="1.4">
              {entry!.definition}
            </Text>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default TermTooltip;